import { 
  Settings2,
  Trash2,
  Bold,
  Italic,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Volume2,
  Mic,
  CheckCircle,
  Circle,
} from "lucide-react";
import { AudioPreviewButton, AudioRecorder } from "./AudioComponents";

// Constants - imported from parent or defined here for independence
const ELEMENT_TYPES = {
  TEXT: "text",
  PEDAGOGICAL_TEXT: "pedagogical_text",
  RECT: "rect",
  CIRCLE: "circle",
  IMAGE: "image",
  SEYES: "seyes",
  GRID: "grid",
  TABLE: "table",
  QRCODE: "qrcode",
  AUDIO: "audio",
  QCM_INTERACTIVE: "qcm_interactive",
  TRIANGLE: "triangle",
  STAR: "star",
  ARROW: "arrow",
  HIGHLIGHTER: "highlighter",
  FREEDRAW: "freedraw",
};

const COLOR_PALETTE = [
  "#000000", "#374151", "#6b7280", "#9ca3af",
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16", "#22c55e",
  "#14b8a6", "#06b6d4", "#0ea5e9", "#3b82f6", "#6366f1", "#8b5cf6",
  "#a855f7", "#d946ef", "#ec4899", "#f43f5e",
  "#fca5a1", "#fdba74", "#fde047", "#bef264", "#86efac", "#5eead4",
  "#7dd3fc", "#a5b4fc", "#c4b5fd", "#f0abfc"
];

const FONT_OPTIONS = [
  { label: "Nunito (Standard)", value: "Nunito" },
  { label: "OpenDyslexic (Dys)", value: "OpenDyslexic" },
  { label: "Lexie Readable (Dys)", value: "Lexie Readable" },
  { label: "Comic Sans MS", value: "Comic Sans MS" },
  { label: "Arial", value: "Arial" },
  { label: "Verdana", value: "Verdana" },
  { label: "Cursive (Ecriture)", value: "Cursive" },
  { label: "Belle Allure", value: "Belle Allure" },
];

export function PropertiesContent({ selectedElement, onUpdate, onDelete }) {
  if (!selectedElement) {
    return (
      <div className="text-center text-slate-400 py-8">
        <Settings2 className="mx-auto mb-2" size={32} />
        <p className="text-sm">Selectionnez un element</p>
      </div>
    );
  }

  const handleChange = (prop, value) => {
    onUpdate(selectedElement.id, { [prop]: value });
  };

  return (
    <div className="space-y-5">
      {/* Delete button */}
      <button
        data-testid="delete-element"
        onClick={() => onDelete(selectedElement.id)}
        className="w-full py-3 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl flex items-center justify-center gap-2"
      >
        <Trash2 size={18} />
        Supprimer
      </button>

      {/* Text content */}
      {selectedElement.type === ELEMENT_TYPES.TEXT && (
        <>
          {/* Textarea */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Texte</label>
            <textarea
              value={selectedElement.text || ""}
              onChange={(e) => handleChange("text", e.target.value)}
              className="w-full h-24 border-2 border-slate-200 rounded-xl px-3 py-2 text-base mt-1 resize-none focus:border-blue-400 outline-none"
            />
          </div>

          {/* Font family */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Police</label>
            <select
              value={selectedElement.fontFamily || "Nunito"}
              onChange={(e) => handleChange("fontFamily", e.target.value)}
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          {/* Font size */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">
              Taille ({selectedElement.fontSize || 24}px)
            </label>
            <input
              type="range"
              min="12"
              max="72"
              value={selectedElement.fontSize || 24}
              onChange={(e) => handleChange("fontSize", parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>

          {/* Text style buttons */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Style</label>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleChange("fontWeight", selectedElement.fontWeight === "bold" ? "normal" : "bold")}
                className={`flex-1 py-2 rounded-lg font-bold transition-colors ${
                  selectedElement.fontWeight === "bold" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Bold size={18} className="mx-auto" />
              </button>
              <button
                onClick={() => handleChange("fontStyle", selectedElement.fontStyle === "italic" ? "normal" : "italic")}
                className={`flex-1 py-2 rounded-lg transition-colors ${
                  selectedElement.fontStyle === "italic" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Italic size={18} className="mx-auto" />
              </button>
              <button
                onClick={() => handleChange("textDecoration", selectedElement.textDecoration === "line-through" ? "none" : "line-through")}
                className={`flex-1 py-2 rounded-lg transition-colors ${
                  selectedElement.textDecoration === "line-through" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <Strikethrough size={18} className="mx-auto" />
              </button>
            </div>
          </div>

          {/* Text alignment */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Alignement</label>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleChange("align", "left")}
                className={`flex-1 py-2 rounded-lg transition-colors ${
                  (selectedElement.align || "left") === "left" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <AlignLeft size={18} className="mx-auto" />
              </button>
              <button
                onClick={() => handleChange("align", "center")}
                className={`flex-1 py-2 rounded-lg transition-colors ${
                  selectedElement.align === "center" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <AlignCenter size={18} className="mx-auto" />
              </button>
              <button
                onClick={() => handleChange("align", "right")}
                className={`flex-1 py-2 rounded-lg transition-colors ${
                  selectedElement.align === "right" ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                <AlignRight size={18} className="mx-auto" />
              </button>
            </div>
          </div>

          {/* Text border/stroke */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Bordure du texte</label>
            <div className="flex gap-2 mt-2 items-center">
              <input
                type="color"
                value={selectedElement.stroke || "#000000"}
                onChange={(e) => handleChange("stroke", e.target.value)}
                className="w-10 h-10 rounded-lg border-2 border-slate-200 cursor-pointer"
              />
              <input
                type="range"
                min="0"
                max="4"
                step="0.5"
                value={selectedElement.strokeWidth || 0}
                onChange={(e) => handleChange("strokeWidth", parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm text-slate-500 w-8">{selectedElement.strokeWidth || 0}</span>
            </div>
          </div>

          {/* Width */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Largeur</label>
            <input
              type="number"
              value={Math.round(selectedElement.width || 200)}
              onChange={(e) => handleChange("width", parseInt(e.target.value))}
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            />
          </div>

          {/* Reading aids toggles */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <label className="text-xs font-bold text-slate-500 uppercase">Aides a la lecture</label>
            
            <label className="flex items-center justify-between p-3 bg-blue-50 rounded-xl cursor-pointer">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <span className="text-blue-500">Syl</span><span className="text-orange-500">la</span><span className="text-blue-500">bes</span>
              </span>
              <input
                type="checkbox"
                checked={selectedElement.showColoredSyllables === true}
                onChange={(e) => handleChange("showColoredSyllables", e.target.checked)}
                className="w-5 h-5 rounded text-blue-500"
              />
            </label>
            
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer">
              <span className="text-sm font-medium text-slate-700">
                Lettres muettes <span className="text-slate-400 opacity-50">e</span>
              </span>
              <input
                type="checkbox"
                checked={selectedElement.showSilentLetters === true}
                onChange={(e) => handleChange("showSilentLetters", e.target.checked)}
                className="w-5 h-5 rounded text-slate-500"
              />
            </label>
            
            <label className="flex items-center justify-between p-3 bg-orange-50 rounded-xl cursor-pointer">
              <span className="text-sm font-medium text-slate-700">Alternance de lignes</span>
              <input
                type="checkbox"
                checked={selectedElement.showLineAlternation === true}
                onChange={(e) => handleChange("showLineAlternation", e.target.checked)}
                className="w-5 h-5 rounded text-orange-500"
              />
            </label>
          </div>
        </>
      )}

      {/* Legacy: Redirect old PEDAGOGICAL_TEXT to TEXT (for backwards compatibility) */}
      {selectedElement.type === "pedagogical_text" && (
        <div className="p-4 bg-yellow-50 rounded-xl text-sm text-yellow-800">
          Cet element utilise l'ancien format. Les nouvelles options de texte sont maintenant integrees a l'outil Texte standard.
        </div>
      )}

      {/* Color - only for elements that have a fill color */}
      {(selectedElement.type === ELEMENT_TYPES.TEXT || 
        selectedElement.type === ELEMENT_TYPES.RECT || 
        selectedElement.type === ELEMENT_TYPES.CIRCLE ||
        selectedElement.type === ELEMENT_TYPES.TRIANGLE ||
        selectedElement.type === ELEMENT_TYPES.STAR ||
        selectedElement.type === ELEMENT_TYPES.ARROW ||
        selectedElement.type === ELEMENT_TYPES.HIGHLIGHTER) && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">Couleur</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {COLOR_PALETTE.map((color) => (
              <button
                key={color}
                onClick={() => handleChange("fill", color)}
                className={`w-8 h-8 rounded-full border-2 transition-all touch-target ${
                  selectedElement.fill === color ? "border-blue-500 scale-110 shadow-lg" : "border-slate-200"
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Stroke color - for shapes with borders */}
      {(selectedElement.type === ELEMENT_TYPES.RECT || 
        selectedElement.type === ELEMENT_TYPES.CIRCLE ||
        selectedElement.type === ELEMENT_TYPES.TRIANGLE ||
        selectedElement.type === ELEMENT_TYPES.STAR ||
        selectedElement.type === ELEMENT_TYPES.ARROW ||
        selectedElement.type === ELEMENT_TYPES.FREEDRAW) && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">Couleur du trait</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {COLOR_PALETTE.slice(0, 15).map((color) => (
              <button
                key={`stroke-${color}`}
                onClick={() => handleChange("stroke", color)}
                className={`w-7 h-7 rounded-full border-2 transition-all ${
                  selectedElement.stroke === color ? "border-blue-500 scale-110 shadow-md" : "border-slate-200"
                }`}
                style={{ backgroundColor: color }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Stroke width - for shapes */}
      {(selectedElement.type === ELEMENT_TYPES.RECT || 
        selectedElement.type === ELEMENT_TYPES.CIRCLE ||
        selectedElement.type === ELEMENT_TYPES.TRIANGLE ||
        selectedElement.type === ELEMENT_TYPES.STAR ||
        selectedElement.type === ELEMENT_TYPES.ARROW ||
        selectedElement.type === ELEMENT_TYPES.FREEDRAW) && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">
            Épaisseur du trait ({selectedElement.strokeWidth || 2}px)
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={selectedElement.strokeWidth || 2}
            onChange={(e) => handleChange("strokeWidth", parseInt(e.target.value))}
            className="w-full mt-2"
          />
        </div>
      )}

      {/* Opacity - for highlighter */}
      {selectedElement.type === ELEMENT_TYPES.HIGHLIGHTER && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">
            Opacité ({Math.round((selectedElement.opacity || 0.6) * 100)}%)
          </label>
          <input
            type="range"
            min="10"
            max="100"
            value={Math.round((selectedElement.opacity || 0.6) * 100)}
            onChange={(e) => handleChange("opacity", parseInt(e.target.value) / 100)}
            className="w-full mt-2"
          />
        </div>
      )}

      {/* Star points */}
      {selectedElement.type === ELEMENT_TYPES.STAR && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">
            Nombre de branches ({selectedElement.numPoints || 5})
          </label>
          <input
            type="range"
            min="3"
            max="12"
            value={selectedElement.numPoints || 5}
            onChange={(e) => handleChange("numPoints", parseInt(e.target.value))}
            className="w-full mt-2"
          />
        </div>
      )}

      {/* Size for shapes */}
      {(selectedElement.type === ELEMENT_TYPES.RECT) && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Largeur</label>
            <input
              type="number"
              value={Math.round(selectedElement.width || 100)}
              onChange={(e) => handleChange("width", parseInt(e.target.value))}
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Hauteur</label>
            <input
              type="number"
              value={Math.round(selectedElement.height || 100)}
              onChange={(e) => handleChange("height", parseInt(e.target.value))}
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            />
          </div>
        </div>
      )}

      {/* Radius for circle */}
      {selectedElement.type === ELEMENT_TYPES.CIRCLE && (
        <div>
          <label className="text-xs font-bold text-slate-500 uppercase">
            Rayon ({selectedElement.radius || 40}px)
          </label>
          <input
            type="range"
            min="20"
            max="100"
            value={selectedElement.radius || 40}
            onChange={(e) => handleChange("radius", parseInt(e.target.value))}
            className="w-full mt-2"
          />
        </div>
      )}

      {/* Seyes properties */}
      {selectedElement.type === ELEMENT_TYPES.SEYES && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Largeur</label>
              <input
                type="number"
                value={Math.round(selectedElement.width || 200)}
                onChange={(e) => handleChange("width", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Hauteur</label>
              <input
                type="number"
                value={Math.round(selectedElement.height || 120)}
                onChange={(e) => handleChange("height", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">
              Interligne ({selectedElement.lineHeight || 8}mm)
            </label>
            <input
              type="range"
              min="6"
              max="12"
              value={selectedElement.lineHeight || 8}
              onChange={(e) => handleChange("lineHeight", parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={selectedElement.showMargin !== false}
              onChange={(e) => handleChange("showMargin", e.target.checked)}
              className="w-5 h-5 rounded"
            />
            <label className="text-sm font-bold text-slate-600">Afficher marge rouge</label>
          </div>
          {/* Texte intégré sur lignes */}
          <div className="pt-3 border-t border-slate-100">
            <label className="text-xs font-bold text-slate-500 uppercase">Texte sur les lignes</label>
            <textarea
              value={selectedElement.text || ""}
              onChange={(e) => handleChange("text", e.target.value)}
              placeholder="Texte cale sur les lignes Seyes..."
              className="w-full h-20 border-2 border-slate-200 rounded-xl px-3 py-2 text-base mt-1 resize-none focus:border-blue-400 outline-none"
              style={{ fontFamily: selectedElement.textFontFamily || "Cursive" }}
            />
            <select
              value={selectedElement.textFontFamily || "Cursive"}
              onChange={(e) => handleChange("textFontFamily", e.target.value)}
              className="w-full h-10 border-2 border-slate-200 rounded-lg px-2 text-sm mt-2"
            >
              {FONT_OPTIONS.map((font) => (
                <option key={font.value} value={font.value}>{font.label}</option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Grid properties */}
      {selectedElement.type === ELEMENT_TYPES.GRID && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Largeur</label>
              <input
                type="number"
                value={Math.round(selectedElement.width || 160)}
                onChange={(e) => handleChange("width", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Hauteur</label>
              <input
                type="number"
                value={Math.round(selectedElement.height || 160)}
                onChange={(e) => handleChange("height", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">
              Taille cellule ({selectedElement.cellSize || 20}px)
            </label>
            <input
              type="range"
              min="10"
              max="40"
              value={selectedElement.cellSize || 20}
              onChange={(e) => handleChange("cellSize", parseInt(e.target.value))}
              className="w-full mt-2"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Type</label>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleChange("gridType", "square")}
                className={`flex-1 py-2 rounded-lg font-bold text-sm ${
                  (selectedElement.gridType || "square") === "square" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                Carreaux
              </button>
              <button
                onClick={() => handleChange("gridType", "dots")}
                className={`flex-1 py-2 rounded-lg font-bold text-sm ${
                  selectedElement.gridType === "dots" ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                Points
              </button>
            </div>
          </div>
          {/* Texte intégré */}
          <div className="pt-3 border-t border-slate-100">
            <label className="text-xs font-bold text-slate-500 uppercase">Texte dans la grille</label>
            <textarea
              value={selectedElement.text || ""}
              onChange={(e) => handleChange("text", e.target.value)}
              placeholder="Texte cale sur la grille..."
              className="w-full h-20 border-2 border-slate-200 rounded-xl px-3 py-2 text-base mt-1 resize-none focus:border-blue-400 outline-none"
            />
          </div>
        </>
      )}

      {/* Table properties */}
      {selectedElement.type === ELEMENT_TYPES.TABLE && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Largeur</label>
              <input
                type="number"
                value={Math.round(selectedElement.width || 240)}
                onChange={(e) => handleChange("width", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Hauteur</label>
              <input
                type="number"
                value={Math.round(selectedElement.height || 120)}
                onChange={(e) => handleChange("height", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Lignes</label>
              <input
                type="number"
                min="1"
                max="10"
                value={selectedElement.rows || 3}
                onChange={(e) => handleChange("rows", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Colonnes</label>
              <input
                type="number"
                min="1"
                max="10"
                value={selectedElement.cols || 3}
                onChange={(e) => handleChange("cols", parseInt(e.target.value))}
                className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
              />
            </div>
          </div>
        </>
      )}

      {/* QR Code properties */}
      {selectedElement.type === ELEMENT_TYPES.QRCODE && (
        <>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">URL / Texte</label>
            <input
              type="text"
              value={selectedElement.qrData || ""}
              onChange={(e) => handleChange("qrData", e.target.value)}
              placeholder="https://..."
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">
              Taille ({selectedElement.width || 100}px)
            </label>
            <input
              type="range"
              min="60"
              max="200"
              value={selectedElement.width || 100}
              onChange={(e) => {
                const size = parseInt(e.target.value);
                handleChange("width", size);
                handleChange("height", size);
              }}
              className="w-full mt-2"
            />
          </div>
        </>
      )}

      {/* Audio properties */}
      {selectedElement.type === ELEMENT_TYPES.AUDIO && (
        <>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Type audio</label>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleChange("audioType", "tts")}
                className={`flex-1 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-1 ${
                  (selectedElement.audioType || "tts") === "tts" ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                <Volume2 size={16} /> TTS
              </button>
              <button
                onClick={() => handleChange("audioType", "recording")}
                className={`flex-1 py-2 rounded-lg font-bold text-sm flex items-center justify-center gap-1 ${
                  selectedElement.audioType === "recording" ? "bg-violet-500 text-white" : "bg-slate-100 text-slate-600"
                }`}
              >
                <Mic size={16} /> Micro
              </button>
            </div>
          </div>
          
          {(selectedElement.audioType || "tts") === "tts" && (
            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">Texte a lire</label>
              <textarea
                value={selectedElement.text || ""}
                onChange={(e) => handleChange("text", e.target.value)}
                placeholder="Ecrivez le texte..."
                className="w-full h-20 border-2 border-slate-200 rounded-xl px-3 py-2 text-base mt-1 focus:border-violet-400 outline-none resize-none"
              />
              <AudioPreviewButton text={selectedElement.text} />
            </div>
          )}
          
          {selectedElement.audioType === "recording" && (
            <AudioRecorder 
              audioData={selectedElement.audioData}
              onRecorded={(data) => handleChange("audioData", data)}
            />
          )}
        </>
      )}

      {/* QCM Interactive properties */}
      {selectedElement.type === ELEMENT_TYPES.QCM_INTERACTIVE && (
        <>
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase">Question</label>
            <input
              type="text"
              value={selectedElement.question || ""}
              onChange={(e) => handleChange("question", e.target.value)}
              placeholder="Votre question..."
              className="w-full h-12 border-2 border-slate-200 rounded-xl px-3 text-base mt-1 focus:border-blue-400 outline-none"
            />
          </div>
          
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase mb-2 block">Options</label>
            {(selectedElement.options || ["A", "B", "C"]).map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2 mb-2">
                <button
                  onClick={() => handleChange("correctIndex", idx)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    selectedElement.correctIndex === idx 
                      ? "bg-green-500 text-white" 
                      : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                  }`}
                  title="Definir comme bonne reponse"
                >
                  {selectedElement.correctIndex === idx ? <CheckCircle size={18} /> : <Circle size={18} />}
                </button>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const newOpts = [...(selectedElement.options || ["A", "B", "C"])];
                    newOpts[idx] = e.target.value;
                    handleChange("options", newOpts);
                  }}
                  className="flex-1 h-10 border-2 border-slate-200 rounded-lg px-3 text-sm focus:border-blue-400 outline-none"
                />
              </div>
            ))}
            <button
              onClick={() => {
                const newOpts = [...(selectedElement.options || []), "Nouvelle option"];
                handleChange("options", newOpts);
              }}
              className="w-full py-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-bold text-slate-600 mt-1"
            >
              + Ajouter option
            </button>
          </div>
          
          <div className="flex items-center gap-3 mt-2">
            <input
              type="checkbox"
              checked={selectedElement.feedbackSound !== false}
              onChange={(e) => handleChange("feedbackSound", e.target.checked)}
              className="w-5 h-5 rounded"
            />
            <label className="text-sm font-bold text-slate-600">Sons de feedback</label>
          </div>
        </>
      )}
    </div>
  );
}

export default PropertiesContent;
